import { useCallback, useContext, useEffect, useState } from 'react';
import ApiContext from '../store/apiContext';
import {
  CreatePrefilledEmailRequestDTO,
  CreatePrefilledEmailResponseDTO,
  PrefilledEmailResponseDTO,
  UpdatePrefilledEmailRequestDTO,
} from '../api';
import { sortTemplates } from '../utils/sortTemplates';
import { getApiErrorMessage } from '../utils/apiError';

const FETCH_ERROR_FALLBACK = 'Nepodařilo se načíst šablony odpovědí.';

export interface IUseEmailTemplates {
  templates: PrefilledEmailResponseDTO[];
  isLoading: boolean;
  error: string | undefined;
  reload: () => void;
  createTemplate: (payload: CreatePrefilledEmailRequestDTO) => Promise<CreatePrefilledEmailResponseDTO>;
  updateTemplate: (id: number, payload: UpdatePrefilledEmailRequestDTO) => Promise<void>;
  deleteTemplate: (id: number) => Promise<void>;
}

export default function useEmailTemplates(): IUseEmailTemplates {
  const { EmailTemplatesApi } = useContext(ApiContext);
  const [templates, setTemplates] = useState<PrefilledEmailResponseDTO[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | undefined>(undefined);
  const [reloadTrigger, setReloadTrigger] = useState<number>(0);

  useEffect(() => {
    let cancelled = false;

    const fetchTemplates = async () => {
      setIsLoading(true);
      setError(undefined);
      try {
        const response = await EmailTemplatesApi.prefilledEmailsControllerGetPrefilledEmails();
        if (cancelled) {
          return;
        }
        setTemplates(sortTemplates(response));
      } catch (err) {
        if (cancelled) {
          return;
        }
        setError(await getApiErrorMessage(err, FETCH_ERROR_FALLBACK));
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchTemplates();

    return () => {
      cancelled = true;
    };
  }, [EmailTemplatesApi, reloadTrigger]);

  const reload = () => setReloadTrigger((prev) => prev + 1);

  // `createTemplate`/`deleteTemplate` deliberately do NOT catch errors here -
  // they propagate rejections to the caller so it can decide how to surface
  // them (e.g. a friendly 409 message vs. a generic fallback), matching this
  // hook's contract of leaving error-message formatting to the consumer for
  // mutations (as opposed to the read path above, whose error is exposed as
  // a ready-to-render string because there's only ever one thing to show).
  const createTemplate = useCallback(
    async (payload: CreatePrefilledEmailRequestDTO): Promise<CreatePrefilledEmailResponseDTO> => {
      const result = await EmailTemplatesApi.prefilledEmailsControllerCreatePrefilledEmail({
        createPrefilledEmailRequestDTO: payload,
      });
      reload();
      return result;
    },
    [EmailTemplatesApi]
  );

  const updateTemplate = useCallback(
    async (id: number, payload: UpdatePrefilledEmailRequestDTO): Promise<void> => {
      await EmailTemplatesApi.prefilledEmailsControllerUpdatePrefilledEmails({
        emailId: id,
        updatePrefilledEmailRequestDTO: payload,
      });
      reload();
    },
    [EmailTemplatesApi]
  );

  const deleteTemplate = useCallback(
    async (id: number): Promise<void> => {
      await EmailTemplatesApi.prefilledEmailsControllerDeletePrefilledEmail({ emailId: id });
      reload();
    },
    [EmailTemplatesApi]
  );

  return { templates, isLoading, error, reload, createTemplate, updateTemplate, deleteTemplate };
}
